import API from '@/services/APIs';
import { AppIcons } from '@/components/common';
import { Routes } from '@/routes';
import { MessageInstance } from 'antd/es/message/interface';
import { TFunction } from 'i18next';
import { envConfig } from '@/configs';

export interface FeatureItem {
  icon: string | any;
  text: React.ReactElement | string;
  mobileText?: React.ReactElement | string;
  target?: string;
  samadKey?: string; // key for visibility check based on universities.json
  onClick?: (universityURL: string) => Promise<void>;
  visible: ServiceStatus;
  enable: boolean;
  query?: any;
  mobileOrder?: number;
}

interface FeatureList {
  nurture: FeatureItem[];
  dormitory: FeatureItem[];
  helpdesk: FeatureItem[];
  health: FeatureItem[];
  sport: FeatureItem[];
  transport: FeatureItem[];
}

const GRAY_STYLES = {
  'filter': 'gray',
  'WebkitFilter': 'grayscale(1)',
};

export const featureList = (
  university: UniversityEntity,
  profile: UserProfileEntity,
  roles: SamadRole[],
  message: MessageInstance,
  t: TFunction
): FeatureList => {

  let allowTeammate: boolean, allowCvv2: boolean, allowDailySale: boolean;

  // New API (Release about 1403-11-02)
  if (profile?.group?.checkTeammate != null) {
    allowCvv2 = !!university?.samad?.cvv2;
    allowTeammate = (profile?.group?.checkTeammate) && !!university?.samad?.teammate;
    allowDailySale = profile?.group?.canDailySale;
  }
  else {
    // Backward Compatible with older API
    allowTeammate = university?.samad?.teammate && roles.includes('ROLE_RS_USER_TEAMMATES');
    allowCvv2 = !!university?.samad?.cvv2;
    allowDailySale = (envConfig.serveMode === 'cloud') ? true : false;
  }

  const dormitoryPaymentVisible = (envConfig.serveMode === 'cloud') ? 1 : (envConfig?.ui?.removeDormitoryPayment ? 0 : 1);

  return {
    nurture: [
      {
        icon: <AppIcons.ReserveSVG />,
        text: t('common:features.food-reserve'),
        target: Routes.FoodReserve,
        visible: 1,
        enable: true,
        mobileOrder: 0,
      },
      {
        icon: <AppIcons.DailySaleSVG style={allowDailySale ? {} : GRAY_STYLES} />,
        text: t('common:features.food-daily-sale'),
        target: allowDailySale ? Routes.FoodDailySale : undefined,
        visible: 1,
        enable: allowDailySale,
      },
      {
        icon: <AppIcons.TwoKeysSVG style={allowCvv2 ? {} : GRAY_STYLES} />,
        text: t('common:features.change-cvv2'),
        target: allowCvv2 ? Routes.UserProfile : undefined,
        visible: 1,
        enable: allowCvv2,
        query: { scope: 'cvv2' },
      },
      {
        icon: <AppIcons.DiningGroupSVG style={allowTeammate ? {} : GRAY_STYLES} />,
        text: t('common:features.teammate'),
        target: allowTeammate ? Routes.FoodTeammate : undefined,
        visible: 1,
        enable: allowTeammate,
      },
      {
        icon: <AppIcons.ReserveReportSVG />,
        text: t('common:features.reserve-status'),
        target: Routes.FoodReservesReport,
        visible: 1,
        enable: true,
      },
      {
        icon: <AppIcons.FunctionReportSVG />,
        text: t('common:features.activity-report'),
        target: Routes.UserActivityReport,
        visible: 1,
        enable: true,
      },
      {
        icon: <AppIcons.AvailableFoodSVG />,
        text: t('common:features.non-subsidized-restaurant'),
        // target: Routes.DEGAGE_FOOD,
        visible: 1,
        enable: false,
      },
      {
        icon: <AppIcons.LockCardSVG />,
        text: t('common:features.card-lock'),
        // target: Routes.MESSAGES,
        visible: 1,
        enable: false
      },
    ],
    dormitory: [
      {
        icon: <AppIcons.DormitorypreRegisterSVG style={{ maxHeight: '4rem' }} />,
        text: t('common:features.dormitory-pre-register'),
        target: Routes.DormitoryPreRegister,
        visible: 1,
        enable: true,
      },
      {
        icon: <AppIcons.DormitoryRegisterSVG style={{ maxHeight: '4rem' }} />,
        text: t('common:features.dormitory-register'),
        target: Routes.DormitoryRegister,
        visible: 1,
        enable: true
      },
      {
        icon: <AppIcons.DormitoryLeaveSVG style={{ maxHeight: '3.25rem' }} />,
        text: t('common:features.dormitory-leave-registeration'),
        target: Routes.DormitoryLeave,
        mobileText: t('common:features.dormitory-leave-registeration-mobile'),
        visible: 1,
        enable: true
      },
      {
        icon: <AppIcons.DormitoryPaymentSVG style={{ maxHeight: '4rem' }} />,
        text: t('common:features.dormitory-payment'),
        onClick: async (universityURL) => {
          try {
            const token = (await API.User.getLoginToken()).payload.loginAsToken;
            const url = `${universityURL}/j_security_check?loginAsToken=${token}&redirect=/student/dormitory/reg/payment/view.rose`;
            window.open(url);
          }
          catch (e) {
            message.error(t('common:errors.get-permission'));
          }
        },
        visible: dormitoryPaymentVisible,
        enable: true
      },
      {
        icon: <AppIcons.DormitoryCardexSVG style={{ maxHeight: '4rem' }} />,
        text: t('common:features.cardex-accommodation'),
        target: Routes.DormitoryCardexes,
        visible: 1,
        enable: true
      },
      {
        icon: <AppIcons.DormitoryLeaveReportSVG style={{ maxHeight: '3.25rem' }} />,
        text: t('common:features.dormitory-leave-report'),
        mobileText: t('common:features.dormitory-leave-report-mobile'),
        target: Routes.DormitoryLeaveReport,
        visible: 1,
        enable: true
      },
      {
        icon: <AppIcons.DormitoryAttendanceReportSVG style={{ maxHeight: '4rem' }} />,
        text: t('common:features.attendance-report'),
        samadKey: 'Attendance',
        target: Routes.DormitoryAttendanceReport,
        visible: 1,
        enable: true
      },
      {
        icon: <AppIcons.DormitoryGuestSVG style={{ maxHeight: '4rem', maxWidth: '100%' }} />,
        text: t('common:features.dormitory-guest'),
        target: undefined,
        visible: 1,
        enable: false
      },
      {
        icon: <AppIcons.DormitoryExtendSVG style={{ maxHeight: '4rem', maxWidth: '100%' }} />,
        text: t('common:features.dormitory-extend'),
        target: undefined,
        visible: 1,
        enable: false
      },
      {
        icon: <AppIcons.DormitoryLaundrySVG style={{ maxHeight: '3.5rem' }} />,
        text: t('common:features.shared-laundry'),
        mobileText: t('common:features.laundry'),
        target: undefined,
        visible: 1,
        enable: false
      },
    ],
    helpdesk: [
      {
        icon: <AppIcons.IssueNewSVG />,
        text: t('common:features.new-request'),
        target: Routes.HelpdeskCreateIssue,
        visible: 1,
        enable: true,
        mobileOrder: 0,
      },
      {
        icon: <AppIcons.IssueSearchSVG />,
        text: (
          <span className="text-xs lg:text-sm">
            {t('common:features.view-requests')}
          </span>
        ),
        target: Routes.HelpdeskIssues,
        visible: 1,
        enable: true,
        mobileOrder: 1,
      },
    ],
    sport: [
      {
        icon: <AppIcons.SportTicketSVG />,
        text: t('common:features.ticket'),
        target: Routes.SportReserve,
        visible: 1,
        enable: true,
      },
      {
        icon: <AppIcons.SportDailySellSVG />,
        text: t('common:features.last-minute'),
        target: Routes.SportDailySale,
        visible: 1,
        enable: true,
      },
      {
        icon: <AppIcons.SportActivitySVG />,
        text: t('common:features.view-history'),
        target: Routes.SportReservesReport,
        visible: 1,
        enable: true,
      },
      {
        icon: <AppIcons.SportPackageSVG />,
        text: t('common:features.subscription-package'),
        // target: Routes.NURTURE_TEAMMATE,
        visible: 1,
        enable: false,
      }
    ],
    health: [
      {
        icon: <AppIcons.ClinicReserveSVG />,
        text: t('common:features.reserve-appointment'),
        target: Routes.HealthReserve,
        visible: 1,
        mobileOrder: 0,
        enable: true
      },
      {
        icon: <AppIcons.ClinicDailySaleSVG />,
        text: t('common:features.last-minute'),
        target: Routes.HealthDailySale,
        visible: 1,
        mobileOrder: 1,
        enable: true
      },
      {
        icon: <AppIcons.ClinicReportSVG />,
        text: t('common:features.view-history'),
        target: Routes.HealthReservesReport,
        visible: 1,
        mobileOrder: 2,
        enable: true
      },
    ],
    transport: [
      {
        icon: <AppIcons.TransportReserveSVG />,
        text: t('common:features.ticket'),
        target: Routes.TransportReserve,
        visible: 1,
        mobileOrder: 0,
        enable: true
      },
      {
        icon: <AppIcons.TransportDailySaleSVG />,
        text: t('common:features.last-minute'),
        target: Routes.TransportDailySale,
        visible: 1,
        mobileOrder: 1,
        enable: true
      },
      {
        icon: <AppIcons.TransportReportSVG />,
        text: t('common:features.view-history'),
        mobileOrder: 2,
        target: Routes.TransportReservesReport,
        visible: 1,
        enable: true
      },
    ],
  };
};
